import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API_BASE } from "@/App";
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Calendar,
  Target, BookOpen, Brain, AlertTriangle, CheckCircle,
  Lock, Zap, BarChart3, Activity, Award, Clock, Loader2
} from "lucide-react";

/**
 * CLARIAS Intelligence Digests
 * Weekly and Monthly preparation intelligence reports
 */
const IntelligenceDigestsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("weekly");
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPro = user?.role === "PRO";

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [weeklyRes, monthlyRes] = await Promise.all([
          managedAxios.get(`${API_BASE}/digests/weekly`, { headers, signal: controller.signal }),
          managedAxios.get(`${API_BASE}/digests/monthly`, { headers, signal: controller.signal })
        ]);

        if (isMounted) {
          setWeeklyData(weeklyRes.data);
          setMonthlyData(monthlyRes.data);
        }
      } catch (err) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        if (isMounted) {
          setError(err.response?.data?.detail || "Failed to load digests");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const fetchDigests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [weeklyRes, monthlyRes] = await Promise.all([
        managedAxios.get(`${API_BASE}/digests/weekly`, { headers }),
        managedAxios.get(`${API_BASE}/digests/monthly`, { headers })
      ]);

      setWeeklyData(weeklyRes.data);
      setMonthlyData(monthlyRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load digests");
    } finally {
      setLoading(false);
    }
  };

  const TrendIcon = ({ direction }) => {
    if (direction === "up") return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (direction === "down") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      safe: "bg-emerald-100 text-emerald-700",
      borderline: "bg-amber-100 text-amber-700",
      unsafe: "bg-red-100 text-red-700",
      unknown: "bg-slate-100 text-slate-600"
    };
    const labels = {
      safe: "🟢 SAFE",
      borderline: "🟡 BORDERLINE",
      unsafe: "🔴 UNSAFE",
      unknown: "—"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.unknown}`}>
        {labels[status] || status}
      </span>
    );
  };

  const LockedCard = ({ title, icon: Icon }) => (
    <Card className="border border-slate-200 bg-slate-50/50 relative overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2 opacity-50">
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium text-slate-600">{title}</span>
        </div>
        <div className="flex items-center justify-center py-4">
          <div className="text-center">
            <Lock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-xs text-slate-500">PRO Feature</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderDigest = (data) => {
    if (!data) return null;
    const { metrics, trends, period, access_level } = data;

    return (
      <div className="space-y-4">
        {/* Period Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-slate-800">{period.label}</span>
          </div>
          {access_level === "PRO" && (
            <Badge className="bg-amber-500 border-0">PRO</Badge>
          )}
        </div>

        {/* Core Metrics - Available to All */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-slate-500">Tests Taken</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-slate-800">{metrics.tests_taken}</span>
                <TrendIcon direction={trends.tests_direction} />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-slate-500">Accuracy</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-slate-800">{metrics.accuracy}%</span>
                <TrendIcon direction={trends.accuracy_direction} />
                <span className={`text-xs ${trends.accuracy_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {trends.accuracy_change > 0 ? '+' : ''}{trends.accuracy_change}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-slate-500">Active Days</span>
              </div>
              <span className="text-2xl font-bold text-slate-800">{metrics.active_days}/{metrics.period_days}</span>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-slate-500">Consistency</span>
              </div>
              <span className="text-2xl font-bold text-slate-800">{metrics.consistency_percent}%</span>
            </CardContent>
          </Card>
        </div>

        {/* Activity Stats */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Activity Summary
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-purple-600">{metrics.mcqs_attempted}</p>
                <p className="text-xs text-slate-500">MCQs Attempted</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{metrics.answers_written}</p>
                <p className="text-xs text-slate-500">Answers Written</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{metrics.tests_taken}</p>
                <p className="text-xs text-slate-500">Tests Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PRO-Only Sections */}
        {access_level === "PRO" ? (
          <>
            {/* Risk Profile */}
            <Card className="border border-purple-200 bg-purple-50/30">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-purple-600" />
                  Risk Profile
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">CSAT Status</p>
                    <StatusBadge status={metrics.csat_status} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">GS Prelims Status</p>
                    <StatusBadge status={metrics.gs_status} />
                  </div>
                </div>
                {metrics.selection_probability !== null && (
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Selection Probability</span>
                      <span className="text-lg font-bold text-purple-700">{metrics.selection_probability}%</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subject Breakdown (Monthly only) */}
            {metrics.subject_breakdown && metrics.subject_breakdown.length > 0 && (
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Subject-wise Performance
                  </h3>
                  <div className="space-y-2">
                    {metrics.subject_breakdown.map((subject, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            subject.status === 'strong' ? 'bg-emerald-500' :
                            subject.status === 'moderate' ? 'bg-amber-500' : 'bg-red-500'
                          }`} />
                          <span className="text-sm text-slate-700">{subject.subject}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{subject.questions} Qs</span>
                          <span className={`text-sm font-medium ${
                            subject.accuracy >= 70 ? 'text-emerald-600' :
                            subject.accuracy >= 50 ? 'text-amber-600' : 'text-red-600'
                          }`}>{subject.accuracy}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Weak Areas */}
            {metrics.weak_areas && metrics.weak_areas.length > 0 && (
              <Card className="border border-red-200 bg-red-50/30">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Weak Areas (Need Attention)
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {metrics.weak_areas.map((area, idx) => (
                      <Badge key={idx} variant="outline" className="border-red-300 text-red-700">
                        {area}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Topper Benchmark */}
            {metrics.vs_topper !== undefined && (
              <Card className="border border-blue-200 bg-blue-50/30">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4 text-blue-600" />
                    vs Top 10% Benchmark
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Topper Accuracy</p>
                      <p className="text-lg font-bold text-blue-700">{metrics.topper_accuracy}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Your Gap</p>
                      <p className={`text-lg font-bold ${metrics.vs_topper >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {metrics.vs_topper > 0 ? '+' : ''}{metrics.vs_topper}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {metrics.recommendations && metrics.recommendations.length > 0 && (
              <Card className="border border-emerald-200 bg-emerald-50/30">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-emerald-600" />
                    CLARIAS Recommendations
                  </h3>
                  <ul className="space-y-2">
                    {metrics.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* FREE User - Show Locked Cards */
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <LockedCard title="Selection Probability" icon={Target} />
              <LockedCard title="Risk Profile" icon={AlertTriangle} />
              <LockedCard title="Subject Breakdown" icon={BarChart3} />
              <LockedCard title="Weak Areas" icon={Brain} />
            </div>
            
            <Card className="border-2 border-dashed border-purple-300 bg-purple-50/50">
              <CardContent className="p-6 text-center">
                <Lock className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <h3 className="font-semibold text-purple-800 mb-2">Unlock Full Intelligence</h3>
                <p className="text-sm text-purple-600 mb-4">
                  PRO users get selection probability, risk analysis, subject breakdown, weak areas, and personalized recommendations.
                </p>
                <Button 
                  onClick={() => navigate("/upgrade")}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Upgrade to PRO
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-slate-600">Loading intelligence digest...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Intelligence Digests</h1>
                <p className="text-sm text-white/80">Weekly & Monthly Progress Reports</p>
              </div>
            </div>
            {isPro && (
              <Badge className="ml-auto bg-amber-500 border-0">PRO</Badge>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("weekly")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "weekly"
                  ? "bg-white text-purple-700"
                  : "bg-white/15 text-white hover:bg-white/25"
              }`}
            >
              📅 Weekly
            </button>
            <button
              onClick={() => setActiveTab("monthly")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === "monthly"
                  ? "bg-white text-purple-700"
                  : "bg-white/15 text-white hover:bg-white/25"
              }`}
            >
              📆 Monthly
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 text-center text-red-700">
              {error}
            </CardContent>
          </Card>
        ) : (
          renderDigest(activeTab === "weekly" ? weeklyData : monthlyData)
        )}
      </div>
    </div>
  );
};

export default IntelligenceDigestsPage;
